/**
 * QR PNG보내기 미리보기 — `getQRCodeDataUrl` 결과와 동일. 투명 배경은 체커보드로 표시.
 * 사용자명 가로 정렬은 캔버스 생성 시 QR 전체 너비 중앙(`canvas.width / 2`)에 그려지며,
 * 이 컴포넌트는 완성된 PNG를 프레임 안에서만 가운데 배치한다.
 */

import React from 'react';
import styled from 'styled-components';

export interface QRGeneratorPreviewProps {
  dataUrl: string | null;
  isTransparent: boolean;
  loading?: boolean;
}

const QRGeneratorPreview: React.FC<QRGeneratorPreviewProps> = ({
  dataUrl,
  isTransparent,
  loading = false,
}) => (
  <PreviewSlot>
    <PreviewFrame $isTransparent={isTransparent}>
      {loading && (
        <LoadingOverlay aria-busy="true">
          <LoadingLabel>…</LoadingLabel>
        </LoadingOverlay>
      )}
      {dataUrl && <PreviewImg src={dataUrl} alt="" />}
    </PreviewFrame>
  </PreviewSlot>
);

const PreviewSlot = styled.div`
  width: fit-content;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CHECKERBOARD = `
  linear-gradient(45deg, #e6e6e6 25%, transparent 25%),
  linear-gradient(-45deg, #e6e6e6 25%, transparent 25%),
  linear-gradient(45deg, transparent 75%, #e6e6e6 75%),
  linear-gradient(-45deg, transparent 75%, #e6e6e6 75%)
`;

const PreviewFrame = styled.div<{ $isTransparent: boolean }>`
  position: relative;
  width: 100%;
  min-height: 100px;
  max-height: min(32vh, 220px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  background-color: ${({ $isTransparent, theme }) =>
    $isTransparent ? theme.colors.color_Alpha_04 : '#FCFBFB'};
  background-image: ${({ $isTransparent }) => ($isTransparent ? CHECKERBOARD : 'none')};
  background-size: ${({ $isTransparent }) => ($isTransparent ? '12px 12px' : 'auto')};
  background-position: ${({ $isTransparent }) => ($isTransparent ? '0 0, 0 6px, 6px -6px, -6px 0' : '0 0')};
  overflow: hidden;
`;

const PreviewImg = styled.img`
  max-width: 100%;
  max-height: min(30vh, 200px);
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  z-index: 1;
  pointer-events: none;
`;

const LoadingLabel = styled.span`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

export default QRGeneratorPreview;
